import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Typography
} from "@material-ui/core";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import withTheme from "@material-ui/core/styles/withTheme";
import { withTranslation } from "react-i18next";
import HeaderInfo from "../../waybee-ui/HeaderInfo/HeaderInfo";
import Group from "../../waybee-ui/Group";
import Divider from "../../waybee-ui/Divider";
import BackButton from "../../components/BackButton";
import StudentService from "../../services/StudentService";
import PeriodPerformanceChart from "./components/PeriodPerformanceChart";
import ChartService from "../../services/ChartService";
import OverallPerformanceChart from "./components/OverallPerformanceChart";
import GradeDistributionChart from "./components/GradeDistributionChart";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import { RADAR_CHART_HEIGHT } from "./components/radarChartConfig";

/** Se os datasets têm `periodId`, restringe ao bimestre selecionado (estatísticas). */
function filterGradeDistributionByPeriod(datasets, selectedStage) {
  if (!selectedStage || !Array.isArray(datasets) || !datasets.length) {
    return datasets;
  }
  const hasPeriod = datasets.some(d => d.periodId != null);
  if (!hasPeriod) {
    return datasets;
  }
  const filtered = datasets.filter(d => d.periodId === selectedStage);
  return filtered.length ? filtered : datasets;
}

const HeaderGrid = styled(Grid)`
  margin-bottom: -20px;
`;

const HeaderGroup = styled(Group)`
  padding-bottom: 0;
`;

const StageText = styled.span`
  font-size: 14px;
  color: ${props => props.color};
  font-weight: bold;
`;

const StageSelect = styled(Select)`
  margin: 8px 16px;
`;

const ChartPanel = styled(Paper)`
  padding: 20px 20px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

class StudentCharts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      student: null,
      stages: [],
      selectedStage: null,
      periodPerformanceData: null,
      overallPerformanceData: null,
      gradeDistributionData: null,
      gradeDistributionDataSelected: null,
      disciplines: [],
      distFilter: {
        type: "classe"
      }
    };
  }

  async componentDidMount() {
    const { match } = this.props;
    const { studentId } = match.params;
    this.getStudent(studentId);
  }

  getTypes = () => {
    const { t } = this.props;
    return [
      {
        // API filter.type=course → TIPO distribuicao_notas_curso (segmento série)
        id: "course",
        name: t("student.charts.series")
      },
      {
        id: "classe",
        name: t("common.labels.class")
      }
    ];
  };

  getStudent = studentId => {
    StudentService.getStudentById(studentId)
      .then(student => {
        this.setState({ student });
        // Boletim/gráficos por RA (não exige usuário Waybee do aluno)
        const ra = student.ra || student.id || studentId;
        this.getChartStages(ra);
      })
      .catch(e => {
        console.error(e);
      });
  };

  getStudentRa = () => {
    const { student } = this.state;
    const { match } = this.props;
    if (!student) return match.params.studentId;
    return student.ra || student.id || match.params.studentId;
  };

  getChartStages = studentId => {
    ChartService.getChartStages(studentId)
      .then(stages => {
        const list = Array.isArray(stages) ? stages : [];
        if (!list.length) {
          this.setState({ stages: [], selectedStage: null });
          return;
        }
        this.setState(
          { stages: list, selectedStage: list[0].id },
          this.getGraphData
        );
      })
      .catch(e => {
        console.error(e);
      });
  };

  onChangeStage = async e => {
    const ra = this.getStudentRa();
    this.setState({ selectedStage: e.target.value }, async () => {
      await this.getOverallPerformanceChart(ra);
      this.getGradeDistributionChart(ra);
    });
  };

  getPeriodPerformanceChart = studentId => {
    ChartService.getPeriodPerformance(studentId)
      .then(periodPerformanceData => {
        this.setState({ periodPerformanceData });
      })
      .catch(e => {
        console.error(e);
      });
  };

  getOverallPerformanceChart = studentId => {
    const { selectedStage } = this.state;

    ChartService.getOverallPerformanceChart(studentId, selectedStage)
      .then(overallPerformanceData => {
        this.setState({ overallPerformanceData });
      })
      .catch(e => {
        console.error(e);
      });
  };

  getGradeDistributionChart = studentId => {
    const { selectedStage, distFilter } = this.state;
    const { theme } = this.props;

    ChartService.getGradeDistribuitionChart(
      studentId,
      selectedStage,
      distFilter.type
    )
      .then(res => {
        const gradeDistributionData = res;

        if (gradeDistributionData) {
          gradeDistributionData.datasets = gradeDistributionData.datasets.map(
            dataset => {
              if (dataset.borderColor && dataset.backgroundColor) {
                return { ...dataset };
              }
              return {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
                ...dataset
              };
            }
          );
        }
        this.setState(
          {
            gradeDistributionData,
            disciplines: gradeDistributionData.disciplines
          },
          this.setupDisciplines
        );
      })
      .catch(e => {
        console.error(e);
      });
  };

  getGraphData = async () => {
    const ra = this.getStudentRa();
    this.getOverallPerformanceChart(ra);
    this.getPeriodPerformanceChart(ra);
    this.getGradeDistributionChart(ra);
  };

  onChangeType = value => {
    const { distFilter } = this.state;
    const ra = this.getStudentRa();
    this.setState({ distFilter: { ...distFilter, type: value } }, () =>
      this.getGradeDistributionChart(ra)
    );
  };

  setupDisciplines = () => {
    const {
      disciplines,
      distFilter,
      gradeDistributionData,
      selectedStage
    } = this.state;

    const disciplineIds = disciplines.map(d => d.id);
    const disciplineId =
      distFilter.discipline && disciplineIds.includes(distFilter.discipline)
        ? distFilter.discipline
        : disciplines[0].id;

    let datasets = gradeDistributionData.datasets.filter(
      dataset => dataset.discipline.id === disciplineId
    );
    datasets = filterGradeDistributionByPeriod(datasets, selectedStage);

    const gradeDistributionDataSelected = {
      labels: gradeDistributionData.labels,
      datasets
    };

    this.setState({
      distFilter: {
        ...distFilter,
        discipline: disciplineId
      },
      gradeDistributionDataSelected
    });
  };

  onChangeDiscipline = val => {
    const { distFilter, gradeDistributionData, selectedStage } = this.state;

    let datasets = gradeDistributionData.datasets.filter(
      dataset => dataset.discipline.id === val
    );
    datasets = filterGradeDistributionByPeriod(datasets, selectedStage);

    const gradeDistributionDataSelected = {
      labels: gradeDistributionData.labels,
      datasets
    };

    this.setState({
      distFilter: { ...distFilter, discipline: val },
      gradeDistributionDataSelected
    });
  };

  render() {
    const {
      student,
      stages,
      selectedStage,
      periodPerformanceData,
      overallPerformanceData,
      gradeDistributionDataSelected,
      distFilter,
      disciplines
    } = this.state;

    const { theme, t } = this.props;
    const types = this.getTypes();

    if (!student) {
      return (
        <Box p={6} textAlign="center">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        <BackButton to={`/student/${student.id}`} />
        <HeaderGrid item xs={12}>
          <HeaderGroup>
            <Box mb={2}>
              <HeaderInfo
                name={student.name}
                info={t("student.raInfo", { ra: student.ra })}
                photo={student.photo}
              />
            </Box>
            <Divider />
          </HeaderGroup>
        </HeaderGrid>
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography
              variant="h5"
              color="primary"
              component="h2"
              style={{ fontWeight: 500 }}
            >
              {t("student.charts.title")}
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="stretch">
            {periodPerformanceData && (
              <Grid item xs={12} md={6}>
                <ChartPanel elevation={1} square>
                  <Typography
                    variant="h6"
                    color="primary"
                    component="h3"
                    gutterBottom
                    style={{ fontWeight: 500, marginBottom: 16 }}
                  >
                    {t("student.charts.byPeriod")}
                  </Typography>
                  <Box flexGrow={1} minHeight={RADAR_CHART_HEIGHT}>
                    <PeriodPerformanceChart data={periodPerformanceData} />
                  </Box>
                </ChartPanel>
              </Grid>
            )}
            {overallPerformanceData && (
              <Grid item xs={12} md={6}>
                <ChartPanel elevation={1} square>
                  <Typography
                    variant="h6"
                    color="primary"
                    component="h3"
                    gutterBottom
                    style={{ fontWeight: 500, marginBottom: 12 }}
                  >
                    {t("student.charts.overall")}
                  </Typography>
                  <Grid container spacing={2} style={{ marginBottom: 8 }}>
                    <Grid item xs={12} sm={8} md={12}>
                      <StageSelect
                        value={selectedStage}
                        onChange={this.onChangeStage}
                        renderValue={selected => {
                          const actualStage = stages.find(
                            stage => stage.id === selected
                          );
                          if (!actualStage) return;
                          // eslint-disable-next-line consistent-return
                          return (
                            <StageText color={theme.palette.primary.main}>
                              {actualStage.name}
                            </StageText>
                          );
                        }}
                        fullWidth
                      >
                        {stages.map(stage => {
                          return (
                            <MenuItem key={stage.id} value={stage.id}>
                              <StageText color={theme.palette.default.main}>
                                {stage.name}
                              </StageText>
                            </MenuItem>
                          );
                        })}
                      </StageSelect>
                    </Grid>
                  </Grid>
                  <Box flexGrow={1} minHeight={RADAR_CHART_HEIGHT}>
                    <OverallPerformanceChart data={overallPerformanceData} />
                  </Box>
                </ChartPanel>
              </Grid>
            )}
            {gradeDistributionDataSelected && (
              <Grid item xs={12}>
                <ChartPanel elevation={1} square>
                  <Typography
                    variant="h6"
                    color="primary"
                    component="h3"
                    gutterBottom
                    style={{ fontWeight: 500, marginBottom: 12 }}
                  >
                    {t("student.charts.distribution")}
                  </Typography>
                  <Grid container spacing={2} style={{ marginBottom: 8 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>{t("common.labels.period")}</InputLabel>
                        <StageSelect
                          value={selectedStage}
                          onChange={this.onChangeStage}
                          renderValue={selected => {
                            const actualStage = stages.find(
                              stage => stage.id === selected
                            );
                            if (!actualStage) return;
                            // eslint-disable-next-line consistent-return
                            return (
                              <StageText color={theme.palette.primary.main}>
                                {actualStage.name}
                              </StageText>
                            );
                          }}
                          fullWidth
                        >
                          {stages.map(stage => (
                            <MenuItem key={stage.id} value={stage.id}>
                              <StageText color={theme.palette.default.main}>
                                {stage.name}
                              </StageText>
                            </MenuItem>
                          ))}
                        </StageSelect>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t("student.charts.classOrSeries")}
                        </InputLabel>
                        <StageSelect
                          value={distFilter.type}
                          onChange={e => this.onChangeType(e.target.value)}
                        >
                          {types.map(type => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </StageSelect>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <SearchableSelect
                        label={t("common.labels.discipline")}
                        options={disciplines}
                        value={
                          disciplines.find(
                            d => d.id === distFilter.discipline
                          ) || null
                        }
                        onChange={discipline =>
                          this.onChangeDiscipline(
                            discipline ? discipline.id : null
                          )}
                        disableClearable
                        getOptionLabel={o => o.name || ""}
                        getOptionSelected={(a, b) => a.id === b.id}
                        noOptionsText={t("common.messages.noDiscipline")}
                      />
                    </Grid>
                  </Grid>
                  <Box flexGrow={1} minHeight={380}>
                    <GradeDistributionChart
                      data={gradeDistributionDataSelected}
                      periodLabel={
                        stages.find(s => s.id === selectedStage)
                          ? stages.find(s => s.id === selectedStage).name
                          : ""
                      }
                      disciplineLabel={
                        disciplines.find(d => d.id === distFilter.discipline)
                          ? disciplines.find(
                              d => d.id === distFilter.discipline
                            ).name
                          : ""
                      }
                    />
                  </Box>
                </ChartPanel>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

StudentCharts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string)
  }).isRequired,
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({ main: PropTypes.string.isRequired })
        .isRequired,
      default: PropTypes.shape({ main: PropTypes.string.isRequired }).isRequired
    }).isRequired
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withTheme(withRouter(withTranslation()(StudentCharts)));
